import { useState } from 'react';
import { Mail, Phone, MapPin, Clock, Send, MessageCircle, Building2, ShieldCheck } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import api from '../utils/api';

const Contact = () => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post('/contact', form);
      toast.success('Message sent successfully');
      setForm({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      title: 'Phone',
      value: '+91 90637 84294',
      href: 'tel:+919063784294',
    },
    {
      icon: Mail,
      title: 'Email',
      value: 'samridhenterprise24@gmail.com',
      href: 'mailto:samridhenterprise24@gmail.com',
    },
    {
      icon: MapPin,
      title: 'Location',
      value: 'Hyderabad, Telangana, India',
    },
    {
      icon: Clock,
      title: 'Working Hours',
      value: 'Mon - Sat: 10:00 AM - 7:00 PM IST',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 pb-16">
      <div className="text-center pt-24 mb-12 px-6">
        <Badge className="bg-amber-100 text-amber-900 border-amber-300 mb-4">
          Contact & Support
        </Badge>
        <h1 className="text-4xl md:text-5xl font-bold text-amber-950 mb-4">
          Talk to the <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-700 to-orange-600">GWAL Team</span>
        </h1>
        <p className="text-lg text-amber-800 max-w-3xl mx-auto">
          For product questions, bulk requirements, or order assistance, our team is ready to help.
        </p>
      </div>

      <div className="max-w-7xl mx-auto px-6">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mb-12">
          {contactInfo.map((item) => (
            <Card key={item.title} className="border border-amber-200 bg-white shadow-sm">
              <CardContent className="p-6 text-center space-y-3">
                <div className="w-11 h-11 rounded-lg bg-amber-100 text-amber-800 mx-auto flex items-center justify-center">
                  <item.icon className="w-5 h-5" />
                </div>
                <h3 className="font-semibold text-amber-950">{item.title}</h3>
                {item.href ? (
                  <a href={item.href} className="text-amber-700 hover:text-amber-900 text-sm">
                    {item.value}
                  </a>
                ) : (
                  <p className="text-amber-700 text-sm">{item.value}</p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="grid lg:grid-cols-2 gap-10">
          <Card className="border border-amber-200 shadow-lg bg-white">
            <CardHeader>
              <CardTitle className="text-3xl font-bold text-amber-950">Send us a Message</CardTitle>
              <p className="text-amber-700 text-sm">We usually respond within working hours.</p>
            </CardHeader>

            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-amber-900 mb-2">Your Name *</label>
                  <Input
                    name="name"
                    required
                    placeholder="Enter your name"
                    className="border-amber-200"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-amber-900 mb-2">Email Address *</label>
                  <Input
                    name="email"
                    type="email"
                    required
                    placeholder="your@email.com"
                    className="border-amber-200"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-amber-900 mb-2">Phone Number</label>
                  <Input
                    name="phone"
                    placeholder="+91 XXXXX XXXXX"
                    className="border-amber-200"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-amber-900 mb-2">Message *</label>
                  <Textarea
                    name="message"
                    required
                    rows={5}
                    className="border-amber-200"
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                  />
                </div>

                <Button
                  type="submit"
                  disabled={sending}
                  className="w-full bg-amber-700 hover:bg-amber-800 text-white text-base py-6"
                >
                  <Send className="w-5 h-5 mr-2" />
                  {sending ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            </CardContent>
          </Card>

          <div className="space-y-6">
            <Card className="border border-green-300 bg-green-50">
              <CardContent className="p-6 text-center space-y-3">
                <div className="w-12 h-12 rounded-lg bg-green-100 text-green-700 mx-auto flex items-center justify-center">
                  <MessageCircle className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-green-900">Prefer WhatsApp?</h3>
                <p className="text-green-800 text-sm">Quick support for urgent queries and bulk orders.</p>
                <Button asChild className="bg-green-600 hover:bg-green-700 text-white">
                  <a href="https://wa.me/919063784294" target="_blank" rel="noopener noreferrer">
                    Chat on WhatsApp
                  </a>
                </Button>
              </CardContent>
            </Card>

            <Card className="border border-amber-200 bg-white shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-amber-950">
                  <Building2 className="w-5 h-5 text-amber-700" />
                  Visit Our Office
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-amber-800 leading-relaxed text-sm">
                  <strong className="text-amber-950">GWAL SPICES</strong><br />
                  6-6-37/3, Devi Nagar Colony, Bansilalpet, Kavadiguda, Hyderabad<br />
                  Telangana - 500080
                </p>
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3806.7272410946925!2d78.48977157493569!3d17.424872683468646!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bcb99b31d7f77a5%3A0xb141c917081cf7bd!2sSamridh%20Enterprise(Gwal%20Spices)!5e0!3m2!1sen!2sin!4v1764842917083!5m2!1sen!2sin"
                  width="100%"
                  height="300"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="GWAL location"
                ></iframe>
              </CardContent>
            </Card>

            <Card className="border border-amber-200 bg-amber-50">
              <CardContent className="p-5 flex items-start gap-3 text-sm">
                <ShieldCheck className="w-5 h-5 text-amber-700 mt-0.5" />
                <p className="text-amber-800">
                  For order issues, keep your order ID ready. Our team can help faster with complete order details.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
